import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Hash, Users, Clock, AlertCircle, CheckCircle,
  RefreshCw, Settings, Zap, Link2, ChevronRight, ChevronDown,
  Search, ExternalLink, Loader2, Shield, Activity, TrendingUp,
  MessageCircle, Bot, ArrowRight, Check, X, Plus, Minus,
  Eye, Send, Unlink, Key
} from 'lucide-react';
import api from '@/api';
import SlackCredentialsConfig from './SlackCredentialsConfig';

// Slack logo SVG component
function SlackLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 127 127" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z" fill="#E01E5A"/>
      <path d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z" fill="#36C5F0"/>
      <path d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z" fill="#2EB67D"/>
      <path d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z" fill="#ECB22E"/>
    </svg>
  );
}

// Helper to format time ago
function formatTimeAgo(dateString) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Severity badge component
function SeverityBadge({ severity }) {
  const colors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200'
  };

  if (!severity) return null;

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${colors[severity] || 'bg-gray-100 text-gray-700'}`}>
      {severity}
    </span>
  );
}

// Connection status indicator
function ConnectionStatus({ status, workspace }) {
  const statusConfig = {
    connected: { color: 'bg-green-500', label: 'Connected', icon: CheckCircle },
    not_configured: { color: 'bg-gray-400', label: 'Not Configured', icon: Settings },
    pending_install: { color: 'bg-yellow-500', label: 'Pending Install', icon: Clock },
    error: { color: 'bg-red-500', label: 'Error', icon: AlertCircle },
    rate_limited: { color: 'bg-orange-500', label: 'Rate Limited', icon: Clock }
  };

  const config = statusConfig[status] || statusConfig.not_configured;
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full ${config.color}`} />
      <span className="text-sm font-medium text-gray-700">{config.label}</span>
      {workspace && (
        <span className="text-sm text-gray-500">({workspace})</span>
      )}
    </div>
  );
}

// Slack thread card component
function ThreadCard({ thread, onInvestigate }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg bg-white hover:border-purple-300 transition-colors">
      <div
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Hash className="w-4 h-4 text-purple-500 flex-shrink-0" />
              <span className="text-sm font-medium text-purple-600">{thread.channel}</span>
              {thread.incident_id && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-mono rounded">
                  {thread.incident_id}
                </span>
              )}
              <SeverityBadge severity={thread.severity} />
            </div>

            <h4 className="font-medium text-gray-900 truncate">
              {thread.title || 'Incident Discussion'}
            </h4>

            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <MessageCircle className="w-3.5 h-3.5" />
                {thread.message_count} messages
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {thread.participants?.length || 0} participants
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {formatTimeAgo(thread.updated)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {thread.url && (
              <a
                href={thread.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 text-gray-400 hover:text-purple-600 rounded-lg hover:bg-purple-50"
                title="Open in Slack"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onInvestigate(thread);
              }}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg flex items-center gap-1.5"
            >
              <Search className="w-3.5 h-3.5" />
              Investigate
            </button>
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          {thread.key_findings && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-1">Key Findings</h5>
              <p className="text-sm text-gray-700">{thread.key_findings}</p>
            </div>
          )}

          {thread.participant_details && thread.participant_details.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-1">Participants</h5>
              <div className="flex flex-wrap gap-1.5">
                {thread.participant_details.map((p, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
                    title={p.email || p.username}
                  >
                    {p.display_name || p.username}
                  </span>
                ))}
              </div>
            </div>
          )}

          {thread.linked_resources && thread.linked_resources.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-1">Linked Resources</h5>
              <div className="flex flex-wrap gap-2">
                {thread.linked_resources.map((r, i) => (
                  <a
                    key={i}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs rounded-lg"
                  >
                    <Link2 className="w-3 h-3" />
                    {r.type}
                  </a>
                ))}
              </div>
            </div>
          )}

          {thread.reactions && Object.keys(thread.reactions).length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-1">Reactions</h5>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(thread.reactions).map(([emoji, count]) => (
                  <span
                    key={emoji}
                    className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
                  >
                    :{emoji}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Channel selector component
function ChannelSelector({ channels, selectedChannels, onChange, loading }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredChannels = channels.filter(ch =>
    ch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ch.topic?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleChannel = (channelId) => {
    if (selectedChannels.includes(channelId)) {
      onChange(selectedChannels.filter(id => id !== channelId));
    } else {
      onChange([...selectedChannels, channelId]);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search channels..."
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading channels...
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            No channels found
          </div>
        ) : (
          filteredChannels.map(channel => (
            <div
              key={channel.id}
              onClick={() => toggleChannel(channel.id)}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                selectedChannels.includes(channel.id) ? 'bg-purple-50' : ''
              }`}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                selectedChannels.includes(channel.id)
                  ? 'bg-purple-600 border-purple-600'
                  : 'border-gray-300'
              }`}>
                {selectedChannels.includes(channel.id) && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{channel.name}</span>
                  {channel.is_private && (
                    <Shield className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </div>
                {channel.purpose && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">{channel.purpose}</p>
                )}
              </div>

              <span className="text-xs text-gray-400">
                {channel.num_members} members
              </span>
            </div>
          ))
        )}
      </div>

      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            {selectedChannels.length} channel{selectedChannels.length !== 1 ? 's' : ''} selected
          </span>
          {selectedChannels.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="text-red-600 hover:text-red-700 font-medium"
            >
              Clear all
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Stats card component
function StatsCard({ icon: Icon, label, value, trend, color = 'purple' }) {
  const colorClasses = {
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
      {trend && (
        <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
          <TrendingUp className="w-3 h-3" />
          {trend}
        </div>
      )}
    </div>
  );
}

// Main Slack Integration Panel
export default function SlackIntegrationPanel({ onInvestigate }) {
  // State
  const [status, setStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [savingChannels, setSavingChannels] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const [setupStep, setSetupStep] = useState('loading'); // 'loading' | 'credentials' | 'connect' | 'connected'
  const [showCredentialsConfig, setShowCredentialsConfig] = useState(false);

  // Fetch status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/slack/status');
      setStatus(response.data);

      if (response.data.monitored_channels) {
        setSelectedChannels(response.data.monitored_channels);
      }

      // Determine setup step based on status
      if (response.data.workspace) {
        setSetupStep('connected');
      } else if (response.data.credentials_configured || response.data.oauth_available) {
        setSetupStep('connect');
      } else if (response.data.needs_credentials) {
        setSetupStep('credentials');
      } else {
        setSetupStep('credentials');
      }

      setError(null);
    } catch (err) {
      console.error('Failed to fetch Slack status:', err);
      // Don't set error for 404 - just means not configured yet
      if (err.response?.status !== 404) {
        setError(err.response?.data?.detail || 'Failed to fetch Slack status');
      }
      setStatus({ configured: false, status: 'not_configured' });
      setSetupStep('credentials');
    }
  }, []);

  // Fetch channels
  const fetchChannels = useCallback(async () => {
    if (!status?.workspace) return;

    try {
      const response = await api.get('/slack/channels');
      setChannels(response.data.channels || []);
    } catch (err) {
      console.error('Failed to fetch channels:', err);
    }
  }, [status?.workspace]);

  // Fetch threads
  const fetchThreads = useCallback(async () => {
    try {
      const response = await api.get('/slack/threads', {
        params: { limit: 20 }
      });
      setThreads(response.data.threads || []);
    } catch (err) {
      console.error('Failed to fetch threads:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchStatus();
      setLoading(false);
    };
    loadData();
  }, [fetchStatus]);

  // Load channels and threads when connected, with auto-refresh
  useEffect(() => {
    if (status?.status === 'connected') {
      fetchChannels();
      fetchThreads();

      // Auto-refresh threads every 30 seconds to pick up background syncs
      const refreshInterval = setInterval(() => {
        fetchThreads();
        fetchStatus(); // Also refresh metrics
      }, 30000);

      return () => clearInterval(refreshInterval);
    }
  }, [status?.status, fetchChannels, fetchThreads]);

  // Sync threads
  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/slack/sync', { since_hours: 24 });
      // Wait a bit for background task, then refresh
      setTimeout(async () => {
        await fetchThreads();
        await fetchStatus();
        setSyncing(false);
      }, 2000);
    } catch (err) {
      console.error('Sync failed:', err);
      setError('Failed to sync threads');
      setSyncing(false);
    }
  };

  // Save channel configuration
  const handleSaveChannels = async () => {
    setSavingChannels(true);
    try {
      await api.post('/slack/channels/configure', {
        channel_ids: selectedChannels
      });
      await fetchStatus();
      setError(null);
    } catch (err) {
      console.error('Failed to save channels:', err);
      setError('Failed to save channel configuration');
    } finally {
      setSavingChannels(false);
    }
  };

  // Start installation
  const handleInstall = async () => {
    try {
      const response = await api.get('/slack/install');
      if (response.data.install_url) {
        window.location.href = response.data.install_url;
      }
    } catch (err) {
      console.error('Failed to start installation:', err);
      setError(err.response?.data?.detail || 'Failed to start Slack installation');
    }
  };

  // Disconnect
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Slack? This will remove all configuration.')) {
      return;
    }

    try {
      await api.delete('/slack/disconnect');
      await fetchStatus();
      setChannels([]);
      setThreads([]);
      setSelectedChannels([]);
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError('Failed to disconnect Slack');
    }
  };

  // Handle investigate from thread
  const handleInvestigateThread = (thread) => {
    const query = thread.title || thread.key_findings || `Investigate incident in ${thread.channel}`;
    if (onInvestigate) {
      onInvestigate(query);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  // Handle credentials configured callback
  const handleCredentialsConfigured = async () => {
    setShowCredentialsConfig(false);
    setSetupStep('connect');
    await fetchStatus();
  };

  // Not connected state - show setup wizard
  if (!status?.workspace) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <SlackLogo className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Slack Integration</h2>
              <p className="text-sm text-gray-500">Connect Slack to investigate incidents where they happen</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Setup Progress Indicator */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            {/* Step 1: Configure */}
            <div className={`flex items-center gap-2 ${setupStep === 'credentials' ? 'text-purple-600' : setupStep === 'connect' || setupStep === 'connected' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                setupStep === 'credentials' ? 'bg-purple-100 border-2 border-purple-600' :
                setupStep === 'connect' || setupStep === 'connected' ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {setupStep === 'connect' || setupStep === 'connected' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Key className="w-4 h-4" />
                )}
              </div>
              <span className="text-sm font-medium">Configure</span>
            </div>

            <div className="flex-1 h-0.5 bg-gray-200 mx-4">
              <div className={`h-full transition-all ${setupStep === 'connect' || setupStep === 'connected' ? 'w-full bg-green-500' : 'w-0'}`} />
            </div>

            {/* Step 2: Connect */}
            <div className={`flex items-center gap-2 ${setupStep === 'connect' ? 'text-purple-600' : setupStep === 'connected' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                setupStep === 'connect' ? 'bg-purple-100 border-2 border-purple-600' :
                setupStep === 'connected' ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {setupStep === 'connected' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
              </div>
              <span className="text-sm font-medium">Connect</span>
            </div>

            <div className="flex-1 h-0.5 bg-gray-200 mx-4">
              <div className={`h-full transition-all ${setupStep === 'connected' ? 'w-full bg-green-500' : 'w-0'}`} />
            </div>

            {/* Step 3: Ready */}
            <div className={`flex items-center gap-2 ${setupStep === 'connected' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                setupStep === 'connected' ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {setupStep === 'connected' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Zap className="w-4 h-4" />
                )}
              </div>
              <span className="text-sm font-medium">Ready</span>
            </div>
          </div>
        </div>

        {/* Step Content */}
        {(setupStep === 'credentials' || showCredentialsConfig) && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <SlackCredentialsConfig
              onConfigured={handleCredentialsConfigured}
            />
          </div>
        )}

        {setupStep === 'connect' && !showCredentialsConfig && (
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200 p-8">
            <div className="max-w-2xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6">
                <SlackLogo className="w-10 h-10" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Connect Your Slack Workspace
              </h3>

              <p className="text-gray-600 mb-8">
                Your credentials are configured! Now connect Slack to automatically sync incident threads from your channels.
                We'll find your #incidents, #alerts, and on-call channels and start importing context.
              </p>

              {/* Features */}
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-left p-4 bg-white rounded-lg border border-purple-200">
                  <Link2 className="w-8 h-8 text-purple-500 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-1">One-Click Connect</h4>
                  <p className="text-sm text-gray-500">
                    Securely connect via Slack OAuth in seconds
                  </p>
                </div>

                <div className="text-left p-4 bg-white rounded-lg border border-purple-200">
                  <MessageCircle className="w-8 h-8 text-purple-500 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-1">Auto-Sync Threads</h4>
                  <p className="text-sm text-gray-500">
                    Incident threads imported automatically from your channels
                  </p>
                </div>

                <div className="text-left p-4 bg-white rounded-lg border border-purple-200">
                  <Zap className="w-8 h-8 text-purple-500 mb-3" />
                  <h4 className="font-semibold text-gray-900 mb-1">Smart Discovery</h4>
                  <p className="text-sm text-gray-500">
                    Auto-detects incident channels like #incidents, #alerts
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <button
                  onClick={handleInstall}
                  className="px-8 py-3 bg-[#4A154B] hover:bg-[#3e1240] text-white font-semibold rounded-lg transition-colors flex items-center gap-3 shadow-lg"
                >
                  <SlackLogo className="w-5 h-5" />
                  Add to Slack
                </button>
              </div>

              {/* Reconfigure option */}
              <button
                onClick={() => setShowCredentialsConfig(true)}
                className="mt-6 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto"
              >
                <Settings className="w-4 h-4" />
                Reconfigure credentials
              </button>
            </div>
          </div>
        )}

        {/* What you'll get */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h3>

          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                setupStep === 'credentials' ? 'bg-purple-600 text-white' :
                setupStep === 'connect' || setupStep === 'connected' ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'
              }`}>
                {setupStep === 'connect' || setupStep === 'connected' ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Configure Slack App Credentials</h4>
                <p className="text-sm text-gray-500">
                  Enter your Slack app's Client ID, Client Secret, and Signing Secret from api.slack.com
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                setupStep === 'connect' ? 'bg-purple-600 text-white' :
                setupStep === 'connected' ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'
              }`}>
                {setupStep === 'connected' ? <Check className="w-4 h-4" /> : '2'}
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Connect via OAuth</h4>
                <p className="text-sm text-gray-500">
                  Click "Add to Slack" to authorize access to your Slack workspace
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                setupStep === 'connected' ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'
              }`}>
                {setupStep === 'connected' ? <Check className="w-4 h-4" /> : '3'}
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Channels auto-discovered</h4>
                <p className="text-sm text-gray-500">
                  We automatically find #incidents, #alerts, #oncall and similar channels
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-semibold text-sm flex-shrink-0">
                4
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Start investigating</h4>
                <p className="text-sm text-gray-500">
                  Incident threads sync automatically. Click any thread to run root cause analysis!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Connected state - show full dashboard
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <SlackLogo className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Slack Integration</h2>
            <ConnectionStatus
              status={status.status}
              workspace={status.workspace?.team_name}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>

          <button
            onClick={handleDisconnect}
            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            title="Disconnect Slack"
          >
            <Unlink className="w-5 h-5" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'threads', label: 'Threads', icon: MessageCircle },
            { id: 'channels', label: 'Channels', icon: Hash },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-1 py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <StatsCard
              icon={MessageCircle}
              label="Threads Ingested"
              value={status.metrics?.threads_ingested || 0}
              color="purple"
            />
            <StatsCard
              icon={Hash}
              label="Channels Monitored"
              value={status.metrics?.channels_monitored || 0}
              color="blue"
            />
            <StatsCard
              icon={Search}
              label="Investigations Run"
              value={status.metrics?.investigations_run || 0}
              color="green"
            />
            <StatsCard
              icon={Send}
              label="Results Posted"
              value={status.metrics?.results_posted || 0}
              color="orange"
            />
          </div>

          {/* Last sync info */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-900">
                    Last sync: {status.metrics?.last_sync_at ? formatTimeAgo(status.metrics.last_sync_at) : 'Never'}
                  </p>
                  {status.metrics?.last_error && (
                    <p className="text-xs text-red-600 mt-0.5">
                      Last error: {status.metrics.last_error}
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={handleSync}
                disabled={syncing}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Quick actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Slack Commands</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-purple-600">/investigate</code>
                <p className="text-xs text-gray-500 mt-1">Start a root cause investigation</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-purple-600">/oncall</code>
                <p className="text-xs text-gray-500 mt-1">Check on-call readiness status</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-purple-600">/devops-status</code>
                <p className="text-xs text-gray-500 mt-1">View integration status</p>
              </div>
            </div>
          </div>

          {/* Recent threads preview */}
          {threads.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Recent Threads</h3>
                <button
                  onClick={() => setActiveTab('threads')}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  View all
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                {threads.slice(0, 3).map(thread => (
                  <ThreadCard
                    key={thread.id || thread.thread_ts}
                    thread={thread}
                    onInvestigate={handleInvestigateThread}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'threads' && (
        <div className="space-y-4">
          {threads.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No threads yet</h3>
              <p className="text-sm text-gray-500 mb-4">
                Sync your incident channels to see threads here
              </p>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg"
              >
                Sync Threads
              </button>
            </div>
          ) : (
            threads.map(thread => (
              <ThreadCard
                key={thread.id || thread.thread_ts}
                thread={thread}
                onInvestigate={handleInvestigateThread}
              />
            ))
          )}
        </div>
      )}

      {activeTab === 'channels' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Monitored Channels</h3>
              <p className="text-sm text-gray-500">
                Select which channels to monitor for incident threads
              </p>
            </div>

            <button
              onClick={handleSaveChannels}
              disabled={savingChannels}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium rounded-lg flex items-center gap-2"
            >
              {savingChannels ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>

          <ChannelSelector
            channels={channels}
            selectedChannels={selectedChannels}
            onChange={setSelectedChannels}
            loading={channels.length === 0}
          />

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-800">Pro Tip</h4>
                <p className="text-sm text-blue-700 mt-1">
                  We recommend monitoring channels like #incidents, #alerts, and #oncall.
                  The bot will automatically ingest threads and extract incident metadata.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Integration Settings</h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <h4 className="font-medium text-gray-900">Auto-ingest threads</h4>
                  <p className="text-sm text-gray-500">
                    Automatically capture new threads from monitored channels
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={status.auto_ingest_enabled}
                    onChange={async (e) => {
                      try {
                        await api.patch('/slack/settings', {
                          auto_ingest_enabled: e.target.checked
                        });
                        await fetchStatus();
                      } catch (err) {
                        console.error('Failed to update setting:', err);
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <h4 className="font-medium text-gray-900">Post investigation results</h4>
                  <p className="text-sm text-gray-500">
                    Automatically post summaries back to incident threads
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={status.post_investigation_results}
                    onChange={async (e) => {
                      try {
                        await api.patch('/slack/settings', {
                          post_investigation_results: e.target.checked
                        });
                        await fetchStatus();
                      } catch (err) {
                        console.error('Failed to update setting:', err);
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Workspace Information</h3>

            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-gray-500">Workspace</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {status.workspace?.team_name}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Team ID</dt>
                <dd className="text-sm font-mono text-gray-900">
                  {status.workspace?.team_id}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Installed</dt>
                <dd className="text-sm text-gray-900">
                  {status.workspace?.installed_at
                    ? new Date(status.workspace.installed_at).toLocaleDateString()
                    : 'Unknown'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Installed By</dt>
                <dd className="text-sm font-mono text-gray-900">
                  {status.workspace?.installed_by || 'Unknown'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Credentials Management */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">App Credentials</h3>
              <p className="text-sm text-gray-600 mb-4">
                Your Slack app credentials are used for OAuth authentication.
                {status?.credentials_configured && ' Credentials are currently configured.'}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${status?.credentials_configured ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span className="text-sm text-gray-700">
                    {status?.credentials_configured ? 'Configured' : 'Not configured'}
                  </span>
                </div>
                <a
                  href="https://api.slack.com/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  Manage on Slack
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
          </div>

          <div className="bg-red-50 rounded-lg border border-red-200 p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-2">Danger Zone</h3>
            <p className="text-sm text-red-700 mb-4">
              Disconnecting will remove all Slack configuration and stop monitoring incident channels.
            </p>
            <button
              onClick={handleDisconnect}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg flex items-center gap-2"
            >
              <Unlink className="w-4 h-4" />
              Disconnect Slack
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
